import React, {useLayoutEffect} from "react";
import _ from "lodash";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5locales_it_IT from "@amcharts/amcharts5/locales/it_IT"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {
  getTooltip,
  getXYChart,
  addCursor,
  getRenderAxisX,
  getRenderAxisY,
  COLORS,
  addLicense
} from "../../../components/Charts/commons";


const getChartHigh = (n) => {
  switch (true) {
    case (n < 2):
      return 'h-24'
    case (n < 5):
      return 'h-48'
    case (n < 8):
      return 'h-72'
    case (n < 11):
      return 'h-96'
    case (n < 14):
      return 'h-128'
    default:
      return 'h-152'
  }
}


function GameBarChart({id, data, statusList, intl, showPrice}) {

  useLayoutEffect(() => {

    addLicense();

    let root = am5.Root.new(id);
    root.setThemes([am5themes_Animated.new(root)])
    root.locale = am5locales_it_IT;
    root.numberFormatter.set("numberFormat", showPrice ? "#,###.€" : "#,###.");

    // Create chart
    let chart = getXYChart(root, {layout: root.verticalLayout})
    addCursor(root, chart)

    // Create axes
    let yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "fullname",
      renderer: getRenderAxisY(root, false, true)
    }))
    // yAxis.renderer.grid.template.set("forceHidden", true)

    let yRenderer = yAxis.get("renderer");
    yRenderer.labels.template.setAll({
      fontSize: 11,
      fontWeight: "bold",
      paddingRight: 20,
    });
    yAxis.data.setAll(data);

    let xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
      calculateTotals: true,
      renderer: getRenderAxisX(root, false),
      extraMax: 0.1,
      min: 0
    }))
    xAxis.get("renderer").labels.template.set("forceHidden", true);

    // Add series
    function createSeries(field, showTotal) {
      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: intl.formatMessage({id:field}),
        xAxis: xAxis,
        yAxis: yAxis,
        valueXField: field,
        stacked: true,
        categoryYField: "fullname",
        fill: COLORS[field],
        maskBullets: true,
        minBulletDistance: 10,
        tooltip: getTooltip(root, `[bold]{categoryY}[/]\n{name}: {valueX}`)
      }))

      series.columns.template.setAll({
        tooltipY: am5.percent(90),
        fillOpacity: 0.7
      });

      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: "{valueX}",
            fill: root.interfaceColors.get("alternativeText"),
            centerY: am5.p50,
            centerX: am5.p50,
            populateText: true
          })
        });
      });

      series.columns.template.onPrivate("width", function(width, target) {
        am5.array.each(target.dataItem.bullets, function(bullet) {
          if (width > 40 || bullet.get("sprite").get("text").includes('valueXTotal')) {
            bullet.get("sprite").show();
          }
          else {
            bullet.get("sprite").hide();
          }
        });
      });

      if (showTotal) {
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationX: 1,
            sprite: am5.Label.new(root, {
              text: "[bold]{valueXTotal} {valueY}[/]",
              centerY: am5.p50,
              centerX: am5.p0,
              populateText: true
            })
          });
        });
      }

      series.data.setAll(data);
      series.appear(1000);

    }

    statusList.forEach((status, index) => {
      createSeries(status, index === statusList.length - 1)
    })

    return () => {
      root.dispose()
    }
  }, [data, id, statusList]) // eslint-disable-line react-hooks/exhaustive-deps

  return <div id={id} className={`w-full ${getChartHigh(data.length)}`}/>
}

export default React.memo(GameBarChart, (props, nextProps) => {
  return _.isEqual(props.data, nextProps.data)
})
